import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPinterestP, faXTwitter } from '@fortawesome/free-brands-svg-icons';
import {
  Facebook,
  Instagram,
  Linkedin,
  Heart,
} from 'lucide-react';
import './Footer.css';
import CreativeFeedback from './CreativeFeedback';
import { useServiceAccess } from '../../context/ServiceAccessContext';

interface FooterServiceLink {
  to: string;
  serviceSlug: string;
  label: string;
}

const ServiceFooterLink: React.FC<{
  to: string;
  serviceSlug: string;
  children: React.ReactNode;
}> = ({ to, serviceSlug, children }) => {
  const { canAccess } = useServiceAccess();
  if (!canAccess(serviceSlug)) return null;
  return <li><Link to={to}>{children}</Link></li>;
};

const Footer: React.FC = () => {
  const [showFeedback, setShowFeedback] = useState(false);
  const { t } = useTranslation('footer');

  const footerServiceLinks: FooterServiceLink[] = [
    { to: '/health-records', serviceSlug: 'health-records', label: t('serviceLinks.healthRecords') },
    { to: '/health-assessment', serviceSlug: 'health-assessment', label: t('serviceLinks.healthAssessment') },
    { to: '/health-summary', serviceSlug: 'health-insights', label: t('serviceLinks.healthInsights') },
    { to: '/insurance-record', serviceSlug: 'insurance-management', label: t('serviceLinks.insuranceManagement') },
    { to: '/medicinereminder', serviceSlug: 'medicine-reminder', label: t('serviceLinks.medicineReminder') },
    { to: '/gymservices', serviceSlug: 'gym-services', label: t('serviceLinks.gymServices') },
    { to: '/child-development', serviceSlug: 'child-development', label: t('serviceLinks.childDevelopment') },
    { to: '/elderly-care', serviceSlug: 'elderly-care', label: t('serviceLinks.elderlyCare') },
  ];

  return (
    <>
      <footer className="modern-footer">
        <div className="footer-container">
          <div className="footer-grid">
            {/* Brand Column */}
            <div className="footer-col brand-col">
              <img src="/images/logo.svg" alt="Infinicare" className="footer-logo" />
              <p className="footer-brand-desc">
                {t('brand')}
              </p>
              <div className="footer-social">
                <a href="https://www.facebook.com/share/1CqDuj6HwP/" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Facebook">
                  <Facebook size={18} />
                </a>
                <a href="https://x.com/InfinicareAI" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="X">
                  <FontAwesomeIcon icon={faXTwitter} style={{ fontSize: 18 }} />
                </a>
                <a href="https://in.pinterest.com/infinicareai/" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Pinterest">
                  <FontAwesomeIcon icon={faPinterestP} style={{ fontSize: 18 }} />
                </a>
                <a href="https://www.instagram.com/infinicare_official/" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="Instagram">
                  <Instagram size={18} />
                </a>
                <a href="https://www.linkedin.com/in/infinicare-ai?utm_source=share_via&utm_content=profile&utm_medium=member_ios" target="_blank" rel="noopener noreferrer" className="social-icon" aria-label="LinkedIn">
                  <Linkedin size={18} />
                </a>
              </div>
            </div>

            {/* Services */}
            <div className="footer-col">
              <h4 className="footer-col-title">{t('sections.services')}</h4>
              <ul className="footer-links">
                {footerServiceLinks.map((link) => (
                  <ServiceFooterLink key={link.to} to={link.to} serviceSlug={link.serviceSlug}>
                    {link.label}
                  </ServiceFooterLink>
                ))}
              </ul>
            </div>

            {/* Legal */}
            <div className="footer-col">
              <h4 className="footer-col-title">{t('sections.legal')}</h4>
              <ul className="footer-links">
                <li><Link to="/terms">{t('legalLinks.termsAndConditions')}</Link></li>
                <li><Link to="/privacy-policy">{t('legalLinks.privacyPolicy')}</Link></li>
                <li><Link to="/customer-policies">{t('legalLinks.customerPolicies')}</Link></li>
                <li><Link to="/ai-health-terms">{t('legalLinks.aiHealthTerms')}</Link></li>
                <li><Link to="/legal">{t('legalLinks.allDocuments')}</Link></li>
              </ul>
            </div>

            {/* Contact column commented out — the three remaining columns
                are rebalanced in Footer.css to match. */}
            {/*
            <div className="footer-col">
              <h4 className="footer-col-title">{t('sections.contactUs')}</h4>
              <ul className="footer-contact">
                <li>
                  <Phone size={16} />
                  <span>{t('contact.phone')}</span>
                </li>
                <li>
                  <Mail size={16} />
                  <span>{t('contact.email')}</span>
                </li>
                <li>
                  <MapPin size={16} />
                  <span>{t('contact.address')}</span>
                </li>
              </ul>
            </div>
            */}
          </div>

          {/* Footer Bottom */}
          <div className="footer-bottom">
            <p className="footer-copyright">
              {t('copyright', { year: new Date().getFullYear() })} Made with
              <Heart size={14} className="heart-icon" />
              in India
            </p>
          </div>
        </div>

        {/* Feedback Button */}
        <button className="feedback-fab" onClick={() => setShowFeedback(true)}>
          Feedback
        </button>
      </footer>

      {/* Feedback Modal */}
      {showFeedback && (
        <div className="feedback-modal-overlay" onClick={() => setShowFeedback(false)}>
          <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>
            <CreativeFeedback onClose={() => setShowFeedback(false)} />
            <button className="feedback-close" onClick={() => setShowFeedback(false)}>
              &times;
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Footer;
